import { Link } from '@inertiajs/react';
import { cn } from '../../lib/cn';

/**
 * Shared list UI for SentenceStudio Index pages (teacher + student).
 * Each visit links to its analysis, carrying source_chapter context.
 *
 * Props:
 *   visits: array of {
 *     id, visited_at,
 *     analysis: { id, text },
 *     source_chapter: { id, title, label, book_id, book_title } | null,
 *     authored_by?: { id, name, role }   (student view only)
 *   }
 *   showAuthor: when true, render the visit author label
 *   basePath: "/teacher/sentence_studio" | "/student/sentence_studio"
 */
export default function VisitHistoryList({ visits, showAuthor = false, basePath }) {
  if (!visits || visits.length === 0) {
    return (
      <div className="py-12 text-center text-gray-500 dark:text-gray-400">
        <p className="text-sm">No sentence analyses yet.</p>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-gray-200 dark:divide-gray-800">
      {visits.map((v) => {
        const href = v.source_chapter
          ? `${basePath}/${v.analysis.id}?from_chapter=${v.source_chapter.id}`
          : `${basePath}/${v.analysis.id}`;

        return (
          <li key={v.id} className="py-4">
            <Link
              href={href}
              className="group block hover:bg-gray-50 dark:hover:bg-gray-900/50 -mx-3 px-3 py-2 rounded-lg transition-colors"
            >
              <p className="text-base text-gray-900 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300 line-clamp-2">
                &ldquo;{v.analysis.text}&rdquo;
              </p>
              <div className="mt-1 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                {v.source_chapter ? (
                  <span>
                    {v.source_chapter.book_title} · {v.source_chapter.label || v.source_chapter.title}
                  </span>
                ) : (
                  <span className="italic">Freeform</span>
                )}
                {showAuthor && v.authored_by && (
                  <span className={cn(
                    'px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide',
                    v.authored_by.role === 'teacher'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                  )}>
                    {v.authored_by.role === 'teacher' ? 'Teacher' : 'You'}
                  </span>
                )}
                <span>{formatDate(v.visited_at)}</span>
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}
